import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  Spinner,
} from "reactstrap";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { Loader } from "rsuite";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { base_url } from "../../constants";
import TableContainer from "../../components/Common/TableContainer";

const QuestionBanksList = () => {
  document.title = "Question Banks | Medicotoon";
  const navigate = useNavigate();
  const localdata = localStorage.getItem("elmatary_admin");
  let adminData = localdata && JSON.parse(localdata);

  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Courses & Units for optional linking
  const [coursesList, setCoursesList] = useState([]);
  const [unitsList, setUnitsList] = useState([]);

  // Add / Edit Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' | 'edit'
  const [bankFormData, setBankFormData] = useState({
    bank_id: "",
    bank_name: "",
    bank_image: "",
    course_id: "",
    unit_id: "",
  });
  const [uploadingImg, setUploadingImg] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch Question Banks from dedicated endpoint
  const getQuestionBanks = async () => {
    setLoading(true);
    try {
      const data_send = {
        admin_id: adminData?.admin_id,
        access_token: adminData?.access_token,
      };

      const res = await axios.post(
        `${base_url}/admin/question_bank/select_question_banks.php`,
        JSON.stringify(data_send)
      );

      if (res && res.message === "Session Expired") {
        localStorage.removeItem("elmatary_admin");
        navigate("/login", { replace: true });
        return;
      }

      if (res && res.status === "success" && Array.isArray(res.message)) {
        setBanks(res.message);
      } else if (Array.isArray(res)) {
        setBanks(res);
      } else {
        setBanks([]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load question banks");
      setBanks([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch All Courses for Linking
  const getCourses = async () => {
    try {
      const data_send = {
        admin_id: adminData?.admin_id,
        access_token: adminData?.access_token,
      };
      const res = await axios.post(
        `${base_url}/admin/courses/select_courses.php`,
        JSON.stringify(data_send)
      );
      if (res && res.status === "success" && Array.isArray(res.message)) {
        setCoursesList(res.message);
      } else if (Array.isArray(res)) {
        setCoursesList(res);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Fetch Units when Course changes
  const fetchUnitsForCourse = async (courseId) => {
    if (!courseId || courseId === "0") {
      setUnitsList([]);
      return;
    }
    try {
      const data_send = {
        admin_id: adminData?.admin_id,
        access_token: adminData?.access_token,
        course_id: courseId,
      };
      const res = await axios.post(
        `${base_url}/admin/courses/select_course_units.php`,
        JSON.stringify(data_send)
      );
      if (res && res.status === "success" && Array.isArray(res.message)) {
        setUnitsList(res.message);
      } else if (Array.isArray(res)) {
        setUnitsList(res);
      } else {
        setUnitsList([]);
      }
    } catch (err) {
      console.error(err);
      setUnitsList([]);
    }
  };

  useEffect(() => {
    getQuestionBanks();
    getCourses();
  }, []);

  // Upload Cover Image
  const handleUploadImage = async (file) => {
    if (!file) return;
    setUploadingImg(true);
    const formData = new FormData();
    formData.append("image", file);
    try {
      const res = await axios.post(
        `${base_url}/admin/image_uploader.php`,
        formData
      );
      if (res) {
        setBankFormData((prev) => ({
          ...prev,
          bank_image: res,
        }));
        toast.success("Cover image uploaded");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload image");
    } finally {
      setUploadingImg(false);
    }
  };

  // Open Create Modal
  const handleOpenAddModal = () => {
    setModalMode("add");
    setBankFormData({
      bank_id: "",
      bank_name: "",
      bank_image: "",
      course_id: "",
      unit_id: "",
    });
    setUnitsList([]);
    setIsModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEditModal = (bank) => {
    setModalMode("edit");
    const courseId = bank.course_id || "";
    const unitId = bank.unit_id || "";
    setBankFormData({
      bank_id: bank.bank_id || bank.course_id,
      bank_name: bank.bank_name || bank.course_name || "",
      bank_image: bank.bank_image || bank.course_photo_url || "",
      course_id: courseId,
      unit_id: unitId,
    });
    if (courseId) {
      fetchUnitsForCourse(courseId);
    } else {
      setUnitsList([]);
    }
    setIsModalOpen(true);
  };

  // Handle Course Select in Modal
  const handleCourseChange = (courseId) => {
    setBankFormData((prev) => ({
      ...prev,
      course_id: courseId,
      unit_id: "",
    }));
    fetchUnitsForCourse(courseId);
  };

  // Form Submit (Add / Edit)
  const handleSubmitBank = async (e) => {
    e.preventDefault();
    if (!bankFormData.bank_name.trim()) {
      toast.warn("Please enter Bank Name");
      return;
    }

    setSubmitting(true);
    try {
      if (modalMode === "add") {
        const data_send = {
          bank_name: bankFormData.bank_name.trim(),
          bank_image: bankFormData.bank_image || "",
          course_id: bankFormData.course_id || "0",
          unit_id: bankFormData.unit_id || "0",
          admin_id: adminData?.admin_id,
          access_token: adminData?.access_token,
        };

        const res = await axios.post(
          `${base_url}/admin/question_bank/create_question_bank.php`,
          JSON.stringify(data_send)
        );

        if (res.status === "success") {
          toast.success(res.message || "Question Bank created successfully");
          setIsModalOpen(false);
          getQuestionBanks();
        } else {
          toast.error(res.message || "Failed to create question bank");
        }
      } else {
        const data_send = {
          bank_id: bankFormData.bank_id,
          bank_name: bankFormData.bank_name.trim(),
          bank_image: bankFormData.bank_image || "",
          course_id: bankFormData.course_id || "0",
          unit_id: bankFormData.unit_id || "0",
          admin_id: adminData?.admin_id,
          access_token: adminData?.access_token,
        };

        const res = await axios.post(
          `${base_url}/admin/question_bank/edit_question_bank.php`,
          JSON.stringify(data_send)
        );

        if (res.status === "success") {
          toast.success(res.message || "Question Bank updated successfully");
          setIsModalOpen(false);
          getQuestionBanks();
        } else {
          toast.error(res.message || "Failed to update question bank");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Server connection error");
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle Visibility
  const showHideBank = async (bank) => {
    const isHidden = bank.hidden === "yes" || bank.course_hidden === "yes";
    try {
      const data_send = {
        bank_id: bank.bank_id || bank.course_id,
        course_id: bank.bank_id || bank.course_id,
        status: isHidden ? "no" : "yes",
        admin_id: adminData?.admin_id,
        access_token: adminData?.access_token,
      };
      const res = await axios.post(
        `${base_url}/admin/courses/show_hide_course.php`,
        JSON.stringify(data_send)
      );
      if (res.status === "success") {
        toast.success(res.message);
        getQuestionBanks();
      } else {
        toast.error(res.message || "Failed to update status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating status");
    }
  };

  // Delete Question Bank
  const handleDeleteBank = async (bankId) => {
    if (window.confirm("Are you sure you want to delete this Question Bank?")) {
      try {
        const data_send = {
          bank_id: bankId,
          admin_id: adminData?.admin_id,
          access_token: adminData?.access_token,
        };
        const res = await axios.post(
          `${base_url}/admin/question_bank/delete_question_bank.php`,
          JSON.stringify(data_send)
        );
        if (res.status === "success") {
          toast.success(res.message || "Question Bank deleted successfully");
          getQuestionBanks();
        } else {
          toast.error(res.message || "Failed to delete Question Bank");
        }
      } catch (err) {
        console.error("Error deleting question bank", err);
        toast.error("Failed to delete Question Bank");
      }
    }
  };

  // Table Columns
  const columns = [
    {
      Header: "#",
      Cell: (cellProps) => <b>{cellProps.cell.row.index + 1}</b>,
    },
    {
      Header: "Cover",
      accessor: "bank_image",
      Cell: ({ row }) => {
        const img = row.original.bank_image || row.original.course_photo_url;
        return (
          <img
            src={img || require("../../assets/images/noimage.png")}
            alt="Bank"
            style={{
              width: "60px",
              height: "50px",
              objectFit: "cover",
              borderRadius: "6px",
              border: "1px solid #eee",
            }}
          />
        );
      },
    },
    {
      Header: "Bank Name",
      accessor: "bank_name",
      Cell: ({ row }) => (
        <div>
          <span className="fw-bold font-size-15 d-block text-primary">
            {row.original.bank_name || row.original.course_name || "No Name"}
          </span>
          <small className="text-muted">
            ID: #{row.original.bank_id || row.original.course_id}
          </small>
        </div>
      ),
    },
    {
      Header: "Linked Course / Unit",
      Cell: ({ row }) => {
        const cName = row.original.course_name;
        const uName = row.original.unit_name;
        return (
          <div>
            {cName ? (
              <span className="badge badge-soft-primary d-block mb-1">
                <i className="bx bx-book-bookmark me-1"></i>
                {cName}
              </span>
            ) : (
              <span className="text-muted font-size-12 d-block mb-1">General Bank</span>
            )}
            {uName && (
              <span className="badge badge-soft-info d-block">
                <i className="bx bx-layer me-1"></i>
                {uName}
              </span>
            )}
          </div>
        );
      },
    },
    {
      Header: "Open Question Bank",
      Cell: ({ row }) => (
        <button
          className="btn btn-sm btn-success d-flex align-items-center gap-1"
          onClick={() =>
            navigate("/question-bank/questions", {
              state: {
                bank_id: row.original.bank_id || row.original.course_id,
                bankdata: row.original,
              },
            })
          }
          title="Open Question Bank Questions"
        >
          <i className="bx bx-help-circle"></i>
          <span>Questions</span>
        </button>
      ),
    },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => handleOpenEditModal(row.original)}
            title="Edit Bank"
          >
            <i className="bx bx-edit-alt"></i> Edit
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => handleDeleteBank(row.original.bank_id || row.original.course_id)}
            title="Delete Bank"
          >
            <i className="bx bx-trash"></i> Delete
          </button>
        </div>
      ),
    },
    {
      Header: "Status",
      Cell: ({ row }) => {
        const item = row.original;
        const isHidden = item.hidden === "yes" || item.course_hidden === "yes";
        return (
          <button
            className={`btn btn-sm ${
              isHidden ? "btn-soft-danger" : "btn-soft-success"
            }`}
            onClick={() => showHideBank(item)}
            title={isHidden ? "Unhide" : "Hide"}
          >
            {isHidden ? (
              <span>
                <i className="bx bx-hide me-1"></i> Hidden
              </span>
            ) : (
              <span>
                <i className="bx bx-show me-1"></i> Visible
              </span>
            )}
          </button>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title="Question Banks"
            breadcrumbItem="Question Banks List"
          />

          <Row>
            <Col lg={12}>
              <Card className="shadow-sm">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                    <h5 className="card-title font-size-16 mb-0 text-primary">
                      <i className="bx bx-help-circle me-1"></i> Question Banks
                    </h5>
                    <button
                      type="button"
                      className="btn btn-success d-flex align-items-center gap-1"
                      onClick={handleOpenAddModal}
                    >
                      <i className="bx bx-plus font-size-18"></i>
                      <span>Create Question Bank</span>
                    </button>
                  </div>

                  {loading ? (
                    <div className="text-center py-5">
                      <Loader />
                      <p className="mt-2 text-muted">Loading Question Banks...</p>
                    </div>
                  ) : banks.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-folder-open font-size-40 mb-2 d-block"></i>
                      <p className="font-size-16 mb-3">No Question Banks found.</p>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={handleOpenAddModal}
                      >
                        <i className="bx bx-plus me-1"></i> Create First Question Bank
                      </button>
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={banks}
                      isGlobalFilter={true}
                      customPageSize={10}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add / Edit Question Bank Modal */}
      <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(!isModalOpen)} centered>
        <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)}>
          <i className="bx bx-help-circle me-1 text-primary"></i>
          {modalMode === "add" ? "Create Question Bank" : "Edit Question Bank"}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmitBank}>
            <div className="mb-3">
              <label className="form-label fw-bold">Question Bank Name *</label>
              <Input
                type="text"
                placeholder="Enter bank title (e.g. Surgery Question Bank)..."
                value={bankFormData.bank_name}
                onChange={(e) =>
                  setBankFormData({
                    ...bankFormData,
                    bank_name: e.target.value,
                  })
                }
                required
              />
            </div>

            <Row className="g-3 mb-3">
              <Col md={6}>
                <label className="form-label fw-bold">Course (Optional)</label>
                <select
                  className="form-select"
                  value={bankFormData.course_id}
                  onChange={(e) => handleCourseChange(e.target.value)}
                >
                  <option value="">General (No Course)</option>
                  {coursesList.map((c) => (
                    <option key={c.course_id} value={c.course_id}>
                      {c.course_name}
                    </option>
                  ))}
                </select>
              </Col>
              <Col md={6}>
                <label className="form-label fw-bold">Unit (Optional)</label>
                <select
                  className="form-select"
                  value={bankFormData.unit_id}
                  onChange={(e) =>
                    setBankFormData({
                      ...bankFormData,
                      unit_id: e.target.value,
                    })
                  }
                  disabled={!bankFormData.course_id}
                >
                  <option value="">All / No Unit</option>
                  {unitsList.map((u) => (
                    <option key={u.unit_id} value={u.unit_id}>
                      {u.unit_name}
                    </option>
                  ))}
                </select>
              </Col>
            </Row>

            <div className="mb-3">
              <label className="form-label fw-bold">Cover Image (Optional)</label>
              <div className="d-flex gap-2 align-items-center">
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files[0]) handleUploadImage(e.target.files[0]);
                  }}
                />
                {uploadingImg && <Spinner size="sm" color="primary" />}
              </div>
              {bankFormData.bank_image && (
                <div className="mt-2">
                  <img
                    src={bankFormData.bank_image}
                    alt="Cover Preview"
                    style={{
                      maxHeight: "100px",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                    }}
                  />
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-success"
                disabled={submitting || uploadingImg}
              >
                {submitting ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Saving...
                  </>
                ) : modalMode === "add" ? (
                  "Create Bank"
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <ToastContainer />
    </React.Fragment>
  );
};

export default QuestionBanksList;
